import apolloClient, { triggerAuthError } from '../api/apolloClient';
import config from '../config/graphqlConfig';
import {
  GET_ALL_RULES_QUERY,
  GET_RULE_QUERY,
  HEALTH_QUERY,
  VALIDATE_RULE_NAME_QUERY,
  IS_CURRENT_USER_ADMIN_QUERY,
  TEST_RULE_QUERY
} from './queries/rules.query';
import { GET_TEMPLATE_DOWNLOAD_URL_QUERY } from './queries/files.query';
import { CREATE_RULE_MUTATION } from './mutations/createRule.mutation';
import {
  UPDATE_RULE_MUTATION,
  DELETE_RULE_MUTATION,
  PROCESS_APPROVAL_MUTATION,
  CANCEL_PENDING_CHANGES_MUTATION
} from './mutations/rules.mutations';
import {
  GENERATE_UPLOAD_URL_MUTATION,
  PARSE_UPLOADED_EXCEL_MUTATION
} from './mutations/files.mutation';
import { REQUEST_ACCESS_MUTATION } from './mutations/access.mutation';

// Export all queries and mutations
export {
  GET_ALL_RULES_QUERY,
  GET_RULE_QUERY,
  HEALTH_QUERY,
  CREATE_RULE_MUTATION,
  UPDATE_RULE_MUTATION,
  DELETE_RULE_MUTATION,
  PROCESS_APPROVAL_MUTATION,
  CANCEL_PENDING_CHANGES_MUTATION,
  IS_CURRENT_USER_ADMIN_QUERY,
  TEST_RULE_QUERY,
  GET_TEMPLATE_DOWNLOAD_URL_QUERY,
  GENERATE_UPLOAD_URL_MUTATION,
  PARSE_UPLOADED_EXCEL_MUTATION,
  REQUEST_ACCESS_MUTATION
};

// ==================== TYPE DEFINITIONS ====================

export type ApprovalStatus = 'pending' | 'approved' | 'rejected';
export type RuleStatus = 'all' | 'submitted' | 'draft' | 'scheduled' | 'active' | 'inactive';
export type ApprovalAction = 'approve' | 'reject';
export type OperationType = 'CREATE' | 'INSERT' | 'UPDATE' | 'DELETE';
export type ChangeStatus = 'CREATED' | 'INSERT' | 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'UPDATE' | 'DELETE';

export interface BoundingBox {
  west: number;
  east: number;
  south: number;
  north: number;
}

export interface PendingChanges {
  operationType: OperationType;
  requestedBy: string;
  requestedAt: string;
  changes: any; // AWSJSON
}

export interface ChangeHistoryEntry {
  status: ChangeStatus;
  timestamp: number;
  operationType: OperationType;
  requestedBy?: string; // Optional for backward compatibility
  modifiedBy: string | null;
  changes: any; // AWSJSON
}

export interface Rule {
  ruleId: string;
  name: string;
  priority: number;
  redirectUrl: string;
  ruleStartFrom?: string;
  ruleEndAt?: string;
  athleteFullName?: string;
  brandName?: string;
  ttl?: number;
  boundingBox?: BoundingBox;
  countries?: string[];
  categories?: string[];
  divisions?: string[];
  gtins?: string[];
  qrs?: string[];
  styleColorCodes?: string[];
  styleCodes?: string[];
  
  // Lifecycle fields
  approvalStatus: ApprovalStatus;
  ruleStatus: RuleStatus;
  
  // Approval metadata
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  
  // Timestamps
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  
  // Pending changes
  pendingChanges?: PendingChanges;
  
  // Change history
  changeHistory?: ChangeHistoryEntry[];

  // Claravine CP code tracking
  claravineStatus?: 'not_set' | 'pending' | 'completed' | 'failed';
  claravineUrl?: string;
  claravineError?: string;
}

export interface RuleInput {
  name: string;
  priority: number;
  redirectUrl: string;
  ruleStartFrom?: string;
  ruleEndAt?: string;
  athleteFullName?: string;
  brandName?: string;
  ttl?: number;
  boundingBox?: BoundingBox;
  countries?: string[];
  categories?: string[];
  divisions?: string[];
  gtins?: string[];
  qrs?: string[];
  styleColorCodes?: string[];
  styleCodes?: string[];
}

export interface UpdateRuleInput {
  name?: string;
  priority?: number;
  redirectUrl?: string;
  ruleStartFrom?: string;
  ruleEndAt?: string;
  athleteFullName?: string;
  brandName?: string;
  ttl?: number;
  boundingBox?: BoundingBox;
  countries?: string[];
  categories?: string[];
  divisions?: string[];
  gtins?: string[];
  qrs?: string[];
  styleColorCodes?: string[];
}

export interface RulesFilter {
  approvalStatus?: ApprovalStatus;
  ruleStatus?: RuleStatus;              // Single status (backward compatibility)
  ruleStatuses?: RuleStatus[];          // Array of statuses (multi-select)
  brandName?: string;
  athleteFullName?: string;
  priority?: number;
  name?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;                     // Field to sort by (e.g., 'updatedAt', 'createdAt')
  sortOrder?: string;                   // Sort direction ('ASC' or 'DESC')
}

export interface RulesResponse {
  success: boolean;
  message?: string;
  items: Rule[];
  count: number;
  totalCount?: number;  // Optional for backward compatibility
  hasMore?: boolean;     // Optional for backward compatibility
}

export interface CreateRuleResult {
  success: boolean;
  message: string;
  ruleId?: string;
}

export interface DeleteResult {
  success: boolean;
  message: string;
  ruleId: string;
}

export interface ApprovalResult {
  success: boolean;
  message: string;
  rule?: Rule;
}

export interface UpdateRuleResult {
  success: boolean;
  message: string;
  ruleId?: string;
  rule?: Rule;
  pendingChangesInfo?: {
    operationType: string;
    requestedBy: string;
    requestedAt: string;
    changes: any; // AWSJSON - contains the actual field changes
  };
}

export interface AdminCheckResult {
  isAdmin: boolean;
  userEmail: string;
  groups: string[];
}

export interface AccessRequestResult {
  success: boolean;
  message: string;
  requestId: string | null;
}


export type ValidationStepStatus = 'pending' | 'success' | 'failed' | 'skipped';
export type OverallValidationStatus = 'passed' | 'failed';

export interface ValidationStep {
  step: number;
  name: string;
  status: ValidationStepStatus;
  duration?: number;
  input?: any;
  output?: any;
  error?: string;
}

export interface TestRuleResult {
  success: boolean;
  overallStatus: OverallValidationStatus;
  message: string;
  ruleId: string;
  ruleName: string;
  steps: ValidationStep[];
  timestamp: string;
}

// ==================== API FUNCTIONS ====================

/**
 * Create a new rule
 */
export async function createRule(input: RuleInput): Promise<CreateRuleResult> {
  const result = await apolloClient.mutate<{ createRule: CreateRuleResult }>({
    mutation: CREATE_RULE_MUTATION,
    variables: { input }
  });

  if (!result.data?.createRule) {
    throw new Error('Failed to create rule');
  }

  const { success, message } = result.data.createRule;
  
  // Check if the backend returned success: false
  if (!success) {
    throw new Error(message || 'Failed to create rule');
  }

  return result.data.createRule;
}

/**
 * Get all rules with optional filtering
 */
export async function getRules(filter?: RulesFilter): Promise<RulesResponse> {
  const result = await apolloClient.query<{ getRules: RulesResponse }>({
    query: GET_ALL_RULES_QUERY,
    variables: { filter },
    fetchPolicy: 'network-only'
  });

  const gqlErrors = (result.error as any)?.graphQLErrors || [];
  if (gqlErrors.some((e: any) => 
    e.errorType === 'UnauthorizedException' || 
    e.message?.includes('not authorized') ||
    e.extensions?.code === 'UNAUTHENTICATED'
  )) {
    triggerAuthError();
    throw new Error('Session expired');
  }

  if (!result.data?.getRules) {
    throw new Error('Failed to fetch rules');
  }

  return result.data.getRules;
}

/**
 * Get a single rule by ID
 */
export async function getRule(ruleId: string): Promise<Rule | null> {
  const result = await apolloClient.query<{ getRule: { success: boolean; message: string; rule: Rule | null } }>({
    query: GET_RULE_QUERY,
    variables: { ruleId },
    fetchPolicy: 'network-only'
  });

  if (!result.data?.getRule) {
    throw new Error('Failed to fetch rule');
  }

  if (!result.data.getRule.success) {
    throw new Error(result.data.getRule.message || 'Failed to fetch rule');
  }

  return result.data.getRule.rule || null;
}

/**
 * Update an existing rule
 */
export async function updateRule(ruleId: string, input: UpdateRuleInput): Promise<UpdateRuleResult> {
  try {
    const result = await apolloClient.mutate<{ updateRule: UpdateRuleResult }>({
      mutation: UPDATE_RULE_MUTATION,
      variables: { ruleId, input }
    });

    if (!result.data?.updateRule) {
      throw new Error('Failed to update rule');
    }

    // Check if update was blocked due to pending changes
    if (!result.data.updateRule.success) {
      // Return the full result so caller can access pendingChangesInfo
      return result.data.updateRule;
    }

    return result.data.updateRule;
  } catch (error: any) {
    // Check if it's a GraphQL error with pending changes message
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      const graphQLError = error.graphQLErrors[0];
      if (graphQLError.message && graphQLError.message.includes('pending changes')) {
        // Try to extract pending changes info from error extensions
        const pendingChangesInfo = graphQLError.extensions?.pendingChangesInfo || null;
        
        // Throw enhanced error with pending changes info
        const enhancedError: any = new Error(graphQLError.message);
        enhancedError.pendingChangesInfo = pendingChangesInfo;
        enhancedError.isPendingChangesError = true;
        throw enhancedError;
      }
    }
    throw error;
  }
}

/**
 * Delete a rule
 */
export async function deleteRule(ruleId: string): Promise<DeleteResult> {
  const result = await apolloClient.mutate<{ deleteRule: DeleteResult }>({
    mutation: DELETE_RULE_MUTATION,
    variables: { ruleId }
  });

  if (!result.data?.deleteRule) {
    throw new Error('Failed to delete rule');
  }

  return result.data.deleteRule;
}

/**
 * Process approval (approve or reject a rule)
 */
export async function processApproval(
  ruleId: string,
  action: ApprovalAction,
  comment?: string
): Promise<ApprovalResult> {
  const result = await apolloClient.mutate<{ processApproval: ApprovalResult }>({
    mutation: PROCESS_APPROVAL_MUTATION,
    variables: { ruleId, action, comment }
  });

  if (!result.data?.processApproval) {
    throw new Error('Failed to process approval');
  }

  return result.data.processApproval;
}

/**
 * Cancel pending changes
 */
export async function cancelPendingChanges(ruleId: string): Promise<ApprovalResult> {
  const result = await apolloClient.mutate<{ cancelPendingChanges: ApprovalResult }>({
    mutation: CANCEL_PENDING_CHANGES_MUTATION,
    variables: { ruleId }
  });

  if (!result.data?.cancelPendingChanges) {
    throw new Error('Failed to cancel pending changes');
  }

  return result.data.cancelPendingChanges;
}

/**
 * Check backend health
 */
export async function checkHealth(): Promise<string> {
  const result = await apolloClient.query<{ health: string }>({
    query: HEALTH_QUERY,
    fetchPolicy: 'network-only'
  });

  return result.data?.health || 'Unknown';
}

/**
 * Validate if a rule name is available
 */
export async function validateRuleName(name: string): Promise<{ isValid: boolean; message: string }> {
  const result = await apolloClient.query<{ validateRuleName: { isValid: boolean; message: string } }>({
    query: VALIDATE_RULE_NAME_QUERY,
    variables: { name },
    fetchPolicy: 'network-only'
  });

  if (!result.data?.validateRuleName) {
    return { isValid: false, message: 'Failed to validate rule name' };
  }

  return result.data.validateRuleName;
}

/**
 * Check if current user is admin (has App.QrRedirectAutomation.Approver group)
 */
export async function checkCurrentUserAdmin(): Promise<AdminCheckResult> {
  const result = await apolloClient.query<{ isCurrentUserAdmin: AdminCheckResult }>({
    query: IS_CURRENT_USER_ADMIN_QUERY,
    fetchPolicy: 'network-only'
  });

  const gqlErrors = (result.error as any)?.graphQLErrors || [];
  if (gqlErrors.some((e: any) => 
    e.errorType === 'UnauthorizedException' || 
    e.message?.includes('not authorized') ||
    e.extensions?.code === 'UNAUTHENTICATED'
  )) {
    triggerAuthError();
    throw new Error('Session expired');
  }

  if (!result.data?.isCurrentUserAdmin) {
    throw new Error('Failed to check user admin status');
  }

  return result.data.isCurrentUserAdmin;
}

/**
 * Test a rule by running the 3-step QR validation flow server-side
 */
export async function testRule(
  ruleId: string,
  location?: { latitude: number; longitude: number },
  productCodeOverride?: string,
): Promise<TestRuleResult> {
  const result = await apolloClient.query<{ testRule: TestRuleResult }>({
    query: TEST_RULE_QUERY,
    variables: { ruleId, location, productCodeOverride: productCodeOverride || undefined },
    fetchPolicy: 'network-only'
  });

  const gqlErrors = (result.error as any)?.graphQLErrors || [];
  if (gqlErrors.some((e: any) => 
    e.errorType === 'UnauthorizedException' || 
    e.message?.includes('not authorized') ||
    e.extensions?.code === 'UNAUTHENTICATED'
  )) {
    triggerAuthError();
    throw new Error('Session expired');
  }

  if (!result.data?.testRule) {
    throw new Error('Failed to test rule');
  }

  return result.data.testRule;
}

// ==================== FILE OPERATIONS ====================

export interface TemplateDownloadUrl {
  url: string;
  filename: string;
}

export interface UploadUrlResult {
  uploadUrl: string;
  fileKey: string;
}

export type ExcelBoundingBox = BoundingBox;

export interface ExcelUrlGroup {
  ruleName: string;
  url: string;
  priority: string;
  styleColorCodes: string[];
  styleCodes: string[];
  gtins: string[];
  qrs: string[];
  startDate: string;
  startTime: string;
  expirationDate: string;
  expirationTime: string;
  noExpiration: boolean;
  country: string;
  category: string;
  division: string;
  brandName: string;
  athleteFullName: string;
  boundingBox: ExcelBoundingBox | null;
  httpsValid: boolean;
  sectionName: string;
}

export interface ExcelUploadResult {
  success: boolean;
  message: string | null;
  data: ExcelUrlGroup[];
  warnings: string[];
}

/**
 * Get a presigned S3 URL for downloading the Excel template
 */
export async function getTemplateDownloadUrl(): Promise<TemplateDownloadUrl> {
  const result = await apolloClient.query<{ getTemplateDownloadUrl: TemplateDownloadUrl }>({
    query: GET_TEMPLATE_DOWNLOAD_URL_QUERY,
    fetchPolicy: 'network-only'
  });

  if (!result.data?.getTemplateDownloadUrl) {
    throw new Error('Failed to get template download URL');
  }

  return result.data.getTemplateDownloadUrl;
}

/**
 * Get a presigned S3 URL for uploading an Excel file
 */
export async function getUploadUrl(filename: string): Promise<UploadUrlResult> {
  const result = await apolloClient.mutate<{ generateUploadUrl: UploadUrlResult }>({
    mutation: GENERATE_UPLOAD_URL_MUTATION,
    variables: { filename }
  });

  if (!result.data?.generateUploadUrl) {
    throw new Error('Failed to generate upload URL');
  }

  return result.data.generateUploadUrl;
}

/**
 * Parse an Excel file that was uploaded to S3
 */
export async function parseUploadedExcel(fileKey: string): Promise<ExcelUploadResult> {
  const result = await apolloClient.mutate<{ parseUploadedExcel: ExcelUploadResult }>({
    mutation: PARSE_UPLOADED_EXCEL_MUTATION,
    variables: { fileKey }
  });

  if (!result.data?.parseUploadedExcel) {
    throw new Error('Failed to parse uploaded Excel');
  }

  return result.data.parseUploadedExcel;
}

/**
 * Request access to the QR Redirect Automation platform via IdentityIQ API.
 * Uses the unauthenticated REST endpoint (/api/request-access) instead of
 * the GraphQL endpoint, which sits behind AppSync OIDC auth.
 */
export async function requestAccess(email: string, comment?: string): Promise<AccessRequestResult> {
  const graphqlUrl = config.graphqlEndpoint;
  const baseUrl = graphqlUrl.replace(/\/graphql$/, '');
  const restUrl = `${baseUrl}/api/request-access`;

  const response = await fetch(restUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, comment }),
  });

  if (!response.ok && response.status !== 200) {
    const text = await response.text();
    let parsed: any;
    try { parsed = JSON.parse(text); } catch { parsed = null; }
    throw new Error(parsed?.message || `Request failed (HTTP ${response.status})`);
  }

  const result: AccessRequestResult = await response.json();
  return result;
}

